import React, { useCallback, useState } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useNotify } from '../../../../../../hooks';
import { useApiClient } from '../../../../../../hooks/useRestAPIHooks';
import { messages } from './constants';

export const DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL =
  'http://lifeomic.com/fhir/document-extraction-v2/review-status';
export const DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL =
  'http://lifeomic.com/fhir/document-extraction-v2/approved-by';
export const DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL =
  'http://lifeomic.com/fhir/document-extraction-v2/approved-at';

type ExtractionFeedbackDecision = 'approved' | 'rejected';

export type ExtractionFeedbackResponse = {
  observation_id: string;
  decision: ExtractionFeedbackDecision;
  deleted: boolean;
  approved_by?: string | null;
  approved_at?: string | null;
};

const getObservationExtensionValue = (
  observation: fhir.Observation | undefined,
  url: string,
  key: 'valueString' | 'valueDateTime',
) =>
  observation?.extension?.find(extension => extension.url === url)?.[key] as
    | string
    | undefined;

const getApprovedBy = (observation: fhir.Observation | undefined) =>
  getObservationExtensionValue(
    observation,
    DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
    'valueString',
  );

const getApprovedAt = (observation: fhir.Observation | undefined) =>
  getObservationExtensionValue(
    observation,
    DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
    'valueDateTime',
  );

const isApprovedExtraction = (observation: fhir.Observation | undefined) =>
  getObservationExtensionValue(
    observation,
    DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
    'valueString',
  ) === 'approved';

export const withApprovedExtractionExtensions = <
  TObservation extends { extension?: fhir.Extension[] },
>(
  observation: TObservation,
  {
    approvedBy,
    approvedAt,
  }: {
    approvedBy: string;
    approvedAt: string;
  },
): TObservation =>
  ({
    ...observation,
    extension: [
      ...(observation.extension ?? []).filter(
        extension =>
          extension.url &&
          ![
            DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
            DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
            DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
          ].includes(extension.url),
      ),
      {
        url: DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
        valueString: 'approved',
      },
      {
        url: DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
        valueString: approvedBy,
      },
      {
        url: DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
        valueDateTime: approvedAt,
      },
    ],
  } as TObservation);

export const ExtractionFeedbackActions = ({
  observation,
  subjectId,
  projectId,
  taskId,
  onApproved,
  onRejected,
}: {
  observation: fhir.Observation;
  subjectId?: string;
  projectId?: string;
  taskId?: string;
  onApproved: (response: ExtractionFeedbackResponse) => void;
  onRejected: (observationId: string) => void;
}) => {
  const { formatMessage } = useIntl();
  const apiClient = useApiClient();
  const [notify] = useNotify();
  const [decisionInFlight, setDecisionInFlight] =
    useState<ExtractionFeedbackDecision | null>(null);
  const [isRejectConfirmOpen, setIsRejectConfirmOpen] = useState(false);

  const observationId = observation.id;
  const isApproved = isApprovedExtraction(observation);
  const approvedBy = getApprovedBy(observation);
  const approvedAt = getApprovedAt(observation);
  const approvedAtDisplay = approvedAt ? moment(approvedAt).format('lll') : '';
  const isDisabled =
    !observationId || !subjectId || !projectId || !!decisionInFlight;

  const submitFeedback = useCallback(
    async (decision: ExtractionFeedbackDecision) => {
      if (!observationId || !subjectId || !projectId) {
        return;
      }
      setDecisionInFlight(decision);
      try {
        const response = (await apiClient.request(
          'POST /v1/agents-v2/document-extraction-v2/observations/:observation_id/feedback',
          {
            observation_id: observationId,
            subject_id: subjectId,
            project_id: projectId,
            decision,
            task_id: taskId,
          },
        )) as { data: ExtractionFeedbackResponse };

        if (decision === 'approved') {
          onApproved(response.data);
          notify(formatMessage(messages.approveExtractionSuccess), 'success');
        } else {
          onRejected(observationId);
          notify(formatMessage(messages.rejectExtractionSuccess), 'success');
        }
      } catch {
        notify(formatMessage(messages.extractionFeedbackError), 'error');
      } finally {
        setDecisionInFlight(null);
        setIsRejectConfirmOpen(false);
      }
    },
    [
      apiClient,
      formatMessage,
      notify,
      observationId,
      onApproved,
      onRejected,
      projectId,
      subjectId,
      taskId,
    ],
  );

  return (
    <Box direction="column" gap={1} marginTop={2} fullWidth>
      {isApproved && approvedBy ? (
        <Text size="subbody">
          {formatMessage(messages.approvedExtraction, {
            user: approvedBy,
            time: approvedAtDisplay,
          })}
        </Text>
      ) : null}
      <Box direction="row" gap={1}>
        <Button
          variant="outlined"
          disabled={isDisabled || isApproved}
          onClick={event => {
            event.stopPropagation();
            submitFeedback('approved');
          }}
        >
          {decisionInFlight === 'approved'
            ? formatMessage(messages.approvingExtraction)
            : formatMessage(messages.approveExtraction)}
        </Button>
        <Button
          variant="outlined"
          color="negative"
          disabled={isDisabled}
          onClick={event => {
            event.stopPropagation();
            setIsRejectConfirmOpen(true);
          }}
        >
          {formatMessage(messages.rejectExtraction)}
        </Button>
      </Box>
      <Modal
        isOpen={isRejectConfirmOpen}
        title={formatMessage(messages.rejectExtractionTitle)}
        onDismiss={() => setIsRejectConfirmOpen(false)}
        actions={
          <>
            <Button
              variant="text"
              onClick={() => setIsRejectConfirmOpen(false)}
              disabled={!!decisionInFlight}
            >
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              color="negative"
              onClick={() => submitFeedback('rejected')}
              disabled={!!decisionInFlight}
            >
              {decisionInFlight === 'rejected'
                ? formatMessage(messages.rejectingExtraction)
                : formatMessage(messages.rejectExtraction)}
            </Button>
          </>
        }
      >
        <Text>{formatMessage(messages.rejectExtractionDescription)}</Text>
      </Modal>
    </Box>
  );
};
